import React, { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import { Chat, Add, Lock } from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import TieredFeatureAccess from "../../../shared-components/TieredFeatureAccess";

const ConversationItemText = ({ conversation, textColor = "text.primary" }) => (
  <ListItemText
    primary={
      <Typography
        variant="body2"
        color={textColor}
        sx={{
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {conversation.title || "Untitled Conversation"}
      </Typography>
    }
    secondary={
      <Typography
        variant="caption"
        color={textColor}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        }}
      >
        {formatDistanceToNow(new Date(conversation.startedAt), {
          addSuffix: true,
        })}
      </Typography>
    }
  />
);

export const ConversationSidebar = ({
  conversations,
  loading,
  currentConversationId,
  onNewConversation,
  hasMore = false,
  onLoadMore,
  usageStats,
}) => {
  const navigate = useNavigate();
  const listRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Preserve scroll position when conversations are updated
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [conversations]);

  const handleConversationClick = (conversationId) => {
    navigate(`/assistant?conversation=${conversationId}`);
  };

  const handleLoadMore = () => {
    if (listRef.current) {
      scrollPositionRef.current = listRef.current.scrollTop;
    }
    onLoadMore();
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 280 },
        maxHeight: { xs: 320, md: "none" },
        height: "100%",
        backgroundColor: "background.paper",
        borderRight: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Conversations
        </Typography>
        <TieredFeatureAccess
          accessOverride={usageStats?.canCreateNewConversation}
          tooltipMessage="Upgrade to create new conversations"
          inlineButton={true}
        >
          <Tooltip title="New conversation">
            <IconButton
              size="small"
              onClick={onNewConversation}
              aria-label="New conversation"
              sx={{
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        </TieredFeatureAccess>
      </Box>

      {/* Conversation List */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          py: 1,
        }}
      >
        {loading ? (
          <Box sx={{ px: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  height: 60,
                  mb: 1,
                  borderRadius: 1,
                  backgroundColor: "action.hover",
                }}
              />
            ))}
          </Box>
        ) : !conversations || conversations.length === 0 ? (
          <Box
            sx={{
              px: 2,
              py: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No conversations yet
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ px: 1 }}>
            {conversations.map((conversation) => {
              const isAccessible = conversation.isAccessible !== false;

              return (
                <React.Fragment key={conversation.id}>
                  {isAccessible ? (
                    // Accessible conversation
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={conversation.id === currentConversationId}
                        onClick={() => handleConversationClick(conversation.id)}
                        sx={{
                          borderRadius: 1,
                          py: 1.5,
                          px: 2,
                          "&.Mui-selected": {
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                          },
                        }}
                      >
                        <Box sx={{ mr: 1.5, minWidth: 24 }}>
                          <Chat
                            fontSize="small"
                            sx={{
                              color:
                                conversation.id === currentConversationId
                                  ? "inherit"
                                  : "primary.main",
                            }}
                          />
                        </Box>
                        <ConversationItemText conversation={conversation} />
                      </ListItemButton>
                    </ListItem>
                  ) : (
                    // Locked conversation - same format as normal but with padlock
                    <ListItem disablePadding>
                      <Tooltip title="Upgrade to access.">
                        <Box
                          sx={{
                            width: "100%",
                            px: 2,
                            py: 1.5,
                            opacity: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box sx={{ mr: 1.5, minWidth: 24 }}>
                              <Lock fontSize="small" color="disabled" />
                            </Box>
                            <ConversationItemText
                              conversation={conversation}
                              textColor="text.disabled"
                            />
                          </Box>
                        </Box>
                      </Tooltip>
                    </ListItem>
                  )}
                  <Divider variant="fullWidth" sx={{ my: 0.5 }} />
                </React.Fragment>
              );
            })}
          </List>
        )}

        {hasMore && !loading && (
          <Box sx={{ px: 2, py: 2 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={handleLoadMore}
            >
              Load More
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
